<script lang="ts">
    import { router, useForm, page } from '@inertiajs/svelte';
    import { Button } from '@/components/ui/button';
    import { Input } from '@/components/ui/input';
    import * as Card from '@/components/ui/card';
    import { Plus, Trash, Pencil, Check, X } from 'lucide-svelte';
    import { toast } from 'svelte-sonner';

    type LineItem = {
        id: string;
        name: string;
        quantity: number;
        selling_price: number;
        total: number;
        sequence: number;
    };

    type Props = {
        relatedRecordId: string;
        relatedRecordType: string; // e.g., "App\\Models\\Job"
        lineItems?: LineItem[];
        reloadKey?: string; // What to reload after changes (defaults to 'lineItems')
        onLineItemsChange?: (lineItems: LineItem[]) => void; // Callback when line items change
        id?: string; // Optional id for section navigation
    };

    let { relatedRecordId, relatedRecordType, lineItems = [], reloadKey = 'lineItems', onLineItemsChange, id }: Props = $props();

    // Local state for line items - allows immediate updates
    let localLineItems = $state<LineItem[]>([...lineItems]);

    // Sync local state with prop changes
    $effect(() => {
        localLineItems = [...lineItems];
    });

    let showAddForm = $state(false);
    let editingId = $state<string | null>(null);

    let addForm = $state(useForm({
        line_itemable_id: relatedRecordId,
        line_itemable_type: relatedRecordType,
        name: '',
        quantity: 1,
        selling_price: 0
    }));

    let editForm = $state(useForm({
        name: '',
        quantity: 1,
        selling_price: 0
    }));

    let deleteForm = $state(useForm({}));

    function startAdd() {
        showAddForm = true;
        $addForm.name = '';
        $addForm.quantity = 1;
        $addForm.selling_price = 0;
    }

    function cancelAdd() {
        showAddForm = false;
        $addForm.reset();
    }

    function submitAdd() {
        $addForm.post(route('line-items.store'), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                // Access the flash data from the response
                const flashData = response.props.flash || {};

                if (flashData.lineItem) {
                    // Update local state immediately
                    localLineItems = [...localLineItems, flashData.lineItem];

                    if (onLineItemsChange) {
                        onLineItemsChange(localLineItems);
                    }

                    toast.success('Line item added');
                    cancelAdd();
                }
            },
            onError: () => {
                toast.error('Failed to add line item');
            }
        });
    }

    function startEdit(item: LineItem) {
        editingId = item.id;
        $editForm.name = item.name;
        $editForm.quantity = item.quantity;
        $editForm.selling_price = item.selling_price;
    }

    function cancelEdit() {
        editingId = null;
        $editForm.reset();
    }


    function submitEdit(itemId: string) {
        $editForm.put(route('line-items.update', { line_item: itemId }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                // Access the flash data from the response
                const flashData = response.props.flash || {};

                if (flashData.lineItem) {
                    // Update local state immediately
                    localLineItems = localLineItems.map(item =>
                        item.id === itemId ? flashData.lineItem : item
                    );

                    // Notify parent component
                    if (onLineItemsChange) {
                        onLineItemsChange(localLineItems);
                    }

                    toast.success('Line item updated');
                    cancelEdit();
                }
            },
            onError: () => {
                toast.error('Failed to update line item');
            }
        });
    }

    function deleteItem(itemId: string) {
        if (!confirm('Are you sure you want to delete this line item?')) return;

        $deleteForm.delete(route('line-items.destroy', { 'line_item': itemId }), {
            preserveScroll: true,
            preserveState: true,
            onSuccess: (response) => {
                // Access the flash data from the response
                const flashData = response.props.flash || {};

                if (flashData.deletedId) {
                    // Update local state immediately
                    localLineItems = localLineItems.filter(item => item.id !== flashData.deletedId);

                    // Notify parent component
                    if (onLineItemsChange) {
                        onLineItemsChange(localLineItems);
                    }

                    toast.success('Line item deleted');
                }
            },
            onError: () => {
                toast.error('Failed to delete line item');
            }
        });
    }

    // Calculate subtotal reactively, including form values being edited/added
    let subtotal = $derived.by(() => {
        let total = 0;

        // Add totals from local line items (excluding the one being edited)
        localLineItems.forEach(item => {
            if (editingId === item.id) {
                // Use form values for the item being edited
                total += $editForm.quantity * $editForm.selling_price;
            } else {
                total += parseFloat(item.total.toString());
            }
        });

        // Add the new item being added
        if (showAddForm) {
            total += $addForm.quantity * $addForm.selling_price;
        }

        return total;
    });
</script>



<Card.Root {id}>

    <Card.Content>
        <Card.Title class="flex items-center justify-between mb-2 text-lg ">
            Line Items
            {#if !showAddForm}
                <Button
                    onclick={startAdd}
                    class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent">
                    <Plus class="h-4 w-4" />
                    Add Line Item
                </Button>
            {/if}
        </Card.Title>
        <div class="overflow-x-auto">
            <table class="w-full">
                <thead>
                <tr class="border-b">
                    <th class="text-left p-2">Name</th>
                    <th class="text-left p-2">Quantity</th>
                    <th class="text-left p-2">Price (each)</th>
                    <th class="text-left p-2">Total</th>
                    <th class="text-right p-2">Actions</th>
                </tr>
                </thead>
                <tbody>
                {#if showAddForm}
                    <tr class="border-b bg-muted/50">
                        <td class="p-2">
                            <Input
                                type="text"
                                bind:value={$addForm.name}
                                placeholder="Item name"
                                class={$addForm.errors.name ? 'border-red-500' : ''}
                            />
                        </td>
                        <td class="p-2">
                            <Input
                                type="number"
                                bind:value={$addForm.quantity}
                                min="1"
                                class={$addForm.errors.quantity ? 'border-red-500' : ''}
                            />
                        </td>
                        <td class="p-2">
                            <Input
                                type="number"
                                bind:value={$addForm.selling_price}
                                min="0"
                                step="0.01"
                                class={$addForm.errors.selling_price ? 'border-red-500' : ''}
                            />
                        </td>
                        <td class="p-2 text-right">
                            £{($addForm.quantity * $addForm.selling_price).toFixed(2)}
                        </td>
                        <td class="p-2">
                            <div class="flex gap-2 justify-end">
                                <Button onclick={submitAdd} size="sm" disabled={$addForm.processing}>
                                    <Check class="h-4 w-4" />
                                </Button>
                                <Button onclick={cancelAdd} size="sm" variant="outline">
                                    <X class="h-4 w-4" />
                                </Button>
                            </div>
                        </td>
                    </tr>
                {/if}

                {#each localLineItems as item (item.id)}
                    <tr class="border-b hover:bg-muted/50">
                        {#if editingId === item.id}
                            <td class="p-2">
                                <Input
                                    type="text"
                                    bind:value={$editForm.name}
                                    class={$editForm.errors.name ? 'border-red-500' : ''}
                                />
                            </td>
                            <td class="p-2">
                                <Input
                                    type="number"
                                    bind:value={$editForm.quantity}
                                    min="1"
                                    class={$editForm.errors.quantity ? 'border-red-500' : ''}
                                />
                            </td>
                            <td class="p-2">
                                <Input
                                    type="number"
                                    bind:value={$editForm.selling_price}
                                    min="0"
                                    step="0.01"
                                    class={$editForm.errors.selling_price ? 'border-red-500' : ''}
                                />
                            </td>
                            <td class="p-2 text-right">
                                £{($editForm.quantity * $editForm.selling_price).toFixed(2)}
                            </td>
                            <td class="p-2">
                                <div class="flex gap-2 justify-end">
                                    <Button onclick={() => submitEdit(item.id)} size="sm"
                                            disabled={$editForm.processing}>
                                        <Check class="h-4 w-4" />
                                    </Button>
                                    <Button onclick={cancelEdit} size="sm" variant="outline">
                                        <X class="h-4 w-4" />
                                    </Button>
                                </div>
                            </td>
                        {:else}
                            <td class="p-2">{item.name}</td>
                            <td class="p-2 text-left">{item.quantity}</td>
                            <td class="p-2 text-left">£{parseFloat(item.selling_price.toString()).toFixed(2)}</td>
                            <td class="p-2 text-left font-medium">£{parseFloat(item.total.toString()).toFixed(2)}</td>
                            <td class="p-2">
                                <div class="flex gap-2 justify-end">
                                    <Button onclick={() => startEdit(item)} size="sm" variant="ghost">
                                        <Pencil class="h-4 w-4" />
                                    </Button>
                                    <Button onclick={() => deleteItem(item.id)} size="sm" variant="ghost">
                                        <Trash class="h-4 w-4" />
                                    </Button>
                                </div>
                            </td>
                        {/if}
                    </tr>
                {/each}

                {#if localLineItems.length === 0 && !showAddForm}
                    <tr>
                        <td colspan="5" class="p-4 text-center text-muted-foreground">
                            No line items yet. Click "Add Line Item" to get started.
                        </td>
                    </tr>
                {/if}
                </tbody>
                {#if localLineItems.length > 0}
                    <tfoot>
                    <tr class="border-t-2">
                        <td colspan="3" class="p-2 text-left font-semibold">Subtotal:</td>
                        <td class="p-2 text-leftfont-bold text-lg">£{subtotal.toFixed(2)}</td>
                        <td></td>
                    </tr>
                    </tfoot>
                {/if}
            </table>
        </div>
    </Card.Content>
</Card.Root>
